<template>
  <router-view :key="authenticatedViewKey"></router-view>
  <!-- 全局语言切换（登录前/后都可见，右上角固定位） -->
  <button
    class="global-locale-toggle"
    @click="toggleLocale"
    :title="t('switch_language')"
  >
    {{ isEn ? '中文' : 'EN' }}
  </button>
</template>

<script setup>
import { researchIdentity, researchRouteAllowed, normalizeResearchPermissions, authToken, setResearchIdentity } from '@/modules/cohort/researchPermissions.js'
import {useStore} from 'vuex';
import {onMounted, onUnmounted, computed, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import { t, isEn, setLocale } from '@/plugins/utils/i18n';

const store = useStore();
const router = useRouter();
const authenticatedViewKey = computed(() => researchIdentity.value.token + ':' + (researchIdentity.value.pending ? 'pending' : JSON.stringify(researchIdentity.value.user?.research_permissions)))

watch(researchIdentity, ({user}) => {
  if (user && !researchRouteAllowed(user, router.currentRoute.value.path)) {
    router.replace(normalizeResearchPermissions(user).edit ? '/cohort/subjects' : '/access-denied')
  }
})
async function onAuthStorage(event) {
  if (!['authToken_org', 'authToken1_org'].includes(event.key) && event.key !== null) return
  setResearchIdentity(null, authToken(), true)
  if (!authToken()) { store.commit('clearUser'); router.replace('/login'); return }
  await store.dispatch('fetchUser')
}
onMounted(() => window.addEventListener('storage', onAuthStorage))
onUnmounted(() => window.removeEventListener('storage', onAuthStorage))

const toggleLocale = () => {
  setLocale(isEn.value ? 'zh-Hans' : 'en');
};

let intervalId = null; // 用于存储定时器ID
let loginCheckIntervalId = null; // 登录状态检查定时器ID
let lastActivityTime = Date.now(); // 记录最后一次用户活动时间
let activityCheckIntervalId = null; // 用户活动检查定时器ID

const refresh_user = () => {
  store.dispatch('fetchUser'); // 调用Vuex action更新用户信息
}

// 记录用户活动
const recordUserActivity = () => {
  lastActivityTime = Date.now();
}

// 检查登录状态（轮询）
const checkLoginStatus = async () => {
  const token = localStorage.getItem('authToken_org');
  const currentPath = router.currentRoute.value.path;
  
  // 如果已经在登录页，不需要检查
  if (currentPath === '/login' || currentPath.includes('/login')) {
    return;
  }
  
  if (!token) {
    // 没有token，清除定时器
    if (loginCheckIntervalId) {
      clearInterval(loginCheckIntervalId);
      loginCheckIntervalId = null;
    }
    return;
  }

  try {
    // 调用/currentuser/接口检查登录状态
    await store.dispatch('fetchUser');

  } catch (error) {
    // 如果返回401，说明登录已过期
    if (error.response && error.response.status === 401) {
      console.log('[登录状态检查] 检测到登录已过期，清除认证信息并跳转到登录页');
      
      // 清除所有定时器，避免重复处理
      if (loginCheckIntervalId) {
        clearInterval(loginCheckIntervalId);
        loginCheckIntervalId = null;
      }
      if (activityCheckIntervalId) {
        clearInterval(activityCheckIntervalId);
        activityCheckIntervalId = null;
      }
      
      store.commit('clearUser');
      localStorage.removeItem('authToken_org');
      localStorage.removeItem('authToken1_org');
      localStorage.removeItem('userOrg');
      
      // 显示提示信息
      const { ElMessage } = await import('element-plus');
      ElMessage.error('登录已过期，请重新登录');
      
      // 重定向到登录页（只在不在登录页时）
      if (router.currentRoute.value.path !== '/login' && !router.currentRoute.value.path.includes('/login')) {
        setTimeout(() => {
          router.push('/login').catch(() => {
            // 如果router.push失败，使用window.location
            window.location.href = '/login';
          });
        }, 1000);
      }
    } else {
      // 其他错误，只记录日志，不处理
      console.warn('[登录状态检查] 检查登录状态时出错:', error.message);
    }
  }
}

// 检查用户活动状态，如果长时间不活动则更频繁地检查登录状态
const checkUserActivity = () => {
  const now = Date.now();
  const inactiveTime = now - lastActivityTime;
  const currentPath = router.currentRoute.value.path;
  
  // 如果已经在登录页，不需要检查
  if (currentPath === '/login' || currentPath.includes('/login')) {
    return;
  }
  
  // 如果用户超过10分钟没有活动，每2分钟检查一次登录状态
  // 否则每5分钟检查一次
  if (inactiveTime > 10 * 60 * 1000) {
    // 用户长时间不活动，更频繁地检查
    if (loginCheckIntervalId) {
      clearInterval(loginCheckIntervalId);
      loginCheckIntervalId = null;
    }
    // 每2分钟检查一次
    loginCheckIntervalId = setInterval(checkLoginStatus, 2 * 60 * 1000);
    console.log('[App] 用户长时间不活动，已切换到每2分钟检查一次登录状态');
  } else {
    // 用户有活动，正常检查频率（每5分钟）
    if (!loginCheckIntervalId) {
      loginCheckIntervalId = setInterval(checkLoginStatus, 5 * 60 * 1000);
      console.log('[App] 用户有活动，已切换到每5分钟检查一次登录状态');
    }
  }
}

onMounted(async () => {
  // 路由 meta 是是否需要登录的唯一事实源。公开页面不启动用户态请求，
  // 避免 /currentuser/ 的 401 拦截器把公开页面重定向到 /login。
  const requiresAuth = router.currentRoute.value.matched.some(record => record.meta.requiresAuth)
  const userData = requiresAuth ? await store.dispatch('fetchUser') : null
  
  // cohort-only 账号强制跳转科研队列
  if (userData?.sleepreport_display_mode === 'cohort') {
    const path = router.currentRoute.value.path
    if (path && !['/access-denied', '/field-dictionary'].includes(path) && !path.startsWith('/cohort') && !path.startsWith('/login')) {
      router.replace('/cohort')
    }
  }
  
  // 监听用户活动事件（鼠标移动、点击、键盘输入等）
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  activityEvents.forEach(event => {
    document.addEventListener(event, recordUserActivity, { passive: true });
  });
  
  // 启动登录状态检查轮询
  const token = localStorage.getItem('authToken_org');
  if (token) {
    // 初始检查：每5分钟检查一次
    loginCheckIntervalId = setInterval(checkLoginStatus, 5 * 60 * 1000);
    console.log('[App] 已启动登录状态检查轮询，每5分钟检查一次');
    
    // 启动用户活动检查（每1分钟检查一次用户活动状态）
    activityCheckIntervalId = setInterval(checkUserActivity, 60 * 1000);
    console.log('[App] 已启动用户活动检查，每1分钟检查一次');
    
    // 立即执行一次登录状态检查
    checkLoginStatus();
  }
  
  // intervalId = setInterval(refresh_user, 10000 * 20); // 每5秒刷新一次用户信息
});

onUnmounted(() => {
  // 清除定时器
  if (intervalId) {
    clearInterval(intervalId);
  }
  if (loginCheckIntervalId) {
    clearInterval(loginCheckIntervalId);
  }
  if (activityCheckIntervalId) {
    clearInterval(activityCheckIntervalId);
  }
  
  // 移除事件监听器
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  activityEvents.forEach(event => {
    document.removeEventListener(event, recordUserActivity);
  });
});
</script>

<style>
/* 全局语言切换按钮（App 级，所有页面可见） */
.global-locale-toggle {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 9999;
  min-width: 52px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #1e40af;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition: all 0.15s ease;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
.global-locale-toggle:hover {
  color: #fff;
  background: #2563eb;
  border-color: #2563eb;
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.28);
}
</style>
